import { useMemo } from 'react';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { ProductListItem } from '@shared/model/product';
import { ROUTE_ADMIN_PRODUCT_BUY_ORDER_BASE } from '@shared/config/routes/routes';
import { Toast } from '@shared/ui/toast/toast';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useRequestsListSharedQuery } from '@entities/request/libs/hooks/use-request-list-shared-query/use-request-list-shared-query';
import { DEFAULT_REQUESTS_LIST_PARAMETERS } from '@entities/request/config/store-constants';
import { useCreateRequestBeforePurchaseMutation } from '@features/product/purchase/api/request-api';
import { CACHE_KEY_MANAGER_CREATE_REQUEST_BEFORE_PURCHASE } from '@features/product/purchase/config/api-constants';
import { RequestsListDataRequest } from '@entities/request/api/request-types';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

export const usePurchaseManager = ({
    product,
    productCounter,
}: {
    product: ProductListItem;
    productCounter: number;
}) => {
    const redirect = useRedirect();
    const user = useCurrentUserSelector();
    const [, , resetRequestsList] = useRequestsListSharedQuery(DEFAULT_REQUESTS_LIST_PARAMETERS);
    const [createRequest, createRequestResult] = useCreateRequestBeforePurchaseMutation({
        fixedCacheKey: CACHE_KEY_MANAGER_CREATE_REQUEST_BEFORE_PURCHASE,
    });

    const ownRequestsParameters = useMemo(() => {
        return {
            companyId: user?.company?.id || 0,
            userId: user?.id,
            productId: product.id,
            statuses: 'order_forming',
        } as RequestsListDataRequest;
    }, [user, product]);

    const [getOwnRequests] = useRequestsListSharedQuery(ownRequestsParameters);

    const handlePurchaseManager = async () => {
        if (!user) {
            return;
        }
        const { data } = await getOwnRequests(ownRequestsParameters);
        const ownValidRequests = (data?.items || []).filter((request) => request.order.status === 'order_forming');
        const hasOwnRequests = Boolean(ownValidRequests.length);
        const orderId = ownValidRequests[0]?.order?.id;
        if (!hasOwnRequests) {
            await createRequest({ productId: product.id, quantity: productCounter, userId: user.id });
            return resetRequestsList();
        }
        if (orderId) {
            handleRedirectToOrder(orderId);
        }
    };

    const handleRedirectToOrder = (orderId: number) => {
        redirect({ url: `${ROUTE_ADMIN_PRODUCT_BUY_ORDER_BASE}/${orderId}` });
    };

    useEffect(() => {
        const orderId = createRequestResult.data?.order.id;
        if (createRequestResult.isSuccess && orderId) {
            createRequestResult.reset();
            handleRedirectToOrder(orderId);
        } else if (createRequestResult.isError) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Не удалось создать запрос на продукт.\n${getQueryError(createRequestResult)?.message}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            createRequestResult.reset();
        }
    }, [createRequestResult.isSuccess, createRequestResult.isError]);

    return {
        handlePurchaseManager,
        ownRequestsParameters,
    };
};
